import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, MailWarning } from "lucide-react";
import SocialAuthButtons from "@/components/SocialAuthButtons";
import TurnstileWidget from "@/components/TurnstileWidget";

const TURNSTILE_ENABLED = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  // When the backend reports the account is unverified, we surface a banner
  // + "resend verification email" button right next to the login form.
  const [unverified, setUnverified] = useState(false);
  const [resending, setResending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (TURNSTILE_ENABLED && !turnstileToken) {
      toast.error("Please complete the verification challenge.");
      return;
    }
    setLoading(true);
    setUnverified(false);
    try {
      const user = await login(email, password, turnstileToken);
      toast.success(`Welcome back, ${user.full_name}`);
      if (user.role === "student") nav("/student");
      else nav("/admin");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      // The backend returns 403 with a message that starts with "Please verify"
      if (err?.response?.status === 403 && /verify your email/i.test(msg)) {
        setUnverified(true);
      }
      toast.error(msg);
      turnstileRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const resendEmail = async () => {
    if (!email) {
      toast.error("Enter your email first");
      return;
    }
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      toast.success("Verification email sent. Please check your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend email");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#F8FAFC]">
      <div className="hidden md:block relative">
        <img src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1740&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A]/85 to-[#2563EB]/70" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_smart-prep-board/artifacts/c8fzp7x9_PrepMasters%20New%20Logo%20Night.png"
              alt="PrepMaster"
              className="h-[1.875rem] w-auto"
            />
          </Link>
          <div>
            <div className="font-display text-3xl font-bold leading-tight">Prepare smarter.<br />Review faster.<br />Master your exam.</div>
            <p className="mt-4 text-white/80">AI-Assisted Board Exam &amp; Civil Service Review Platform.</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8 border-slate-200">
          <h1 className="font-display text-3xl font-bold tracking-tight">Welcome back</h1>
          <p className="text-slate-600 mt-1">Login to continue your review.</p>

          <div className="mt-6">
            <SocialAuthButtons />
          </div>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
            <span className="text-xs uppercase tracking-wider text-slate-500">or login with email</span>
            <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
          </div>

          <form className="space-y-4" onSubmit={submit}>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="login-email-input" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-blue-700 font-semibold hover:underline" data-testid="login-forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password-input" />
            </div>
            {unverified && (
              <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm" data-testid="login-unverified-banner">
                <div className="flex items-start gap-2">
                  <MailWarning className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-amber-800">Please verify your email</div>
                    <p className="text-amber-700 mt-0.5 text-xs">
                      We sent a verification link to <span className="font-semibold">{email}</span>. Click it before logging in.
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full mt-2 border-amber-300 text-amber-800 hover:bg-amber-100"
                  onClick={resendEmail}
                  disabled={resending}
                  data-testid="login-resend-verification-btn"
                >
                  {resending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                  Resend verification email
                </Button>
              </div>
            )}
            <Button type="submit" className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-11" disabled={loading || (TURNSTILE_ENABLED && !turnstileToken)} data-testid="login-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Login"}
            </Button>
            <TurnstileWidget ref={turnstileRef} onToken={setTurnstileToken} action="login" />
          </form>
          <p className="mt-6 text-sm text-slate-600 text-center">
            Don't have an account?{" "}
            <Link to="/register" className="text-blue-700 font-semibold hover:underline">Register</Link>
          </p>
          <p className="mt-2 text-sm text-slate-600 text-center">
            <Link to="/" className="hover:underline">← Back to home</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
